import { MessageWidgetConfig as ApiMessageWidgetConfig } from "@closerplatform/spinner-openapi";
import { flow, Instance, types } from "mobx-state-tree";
import { LanguageOption } from "@fidget/common/components/form/languagePicker/languagePicker";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import { HttpStatusCode } from "@fidget/common/utils/httpStatusCodes";
import { servicesGetters, uiStoreGetter } from "../utils";
import { LocaleMessageWidgetsMap } from "./LocaleMessageWidgetsMap";
import {
  IMessageWidgetConfig,
  MessageWidgetConfig,
  parseMessageWidgetConfig
} from "./MessageWidgetConfig";

export const MessageWidgetsStore = types
  .model("MessageWidgetsStore", {
    localeMessageWidgets: types.map(LocaleMessageWidgetsMap)
  })
  .views(servicesGetters)
  .views(uiStoreGetter)
  .views((self) => {
    const views = {
      getMessageWidgets(locale: string, tagGroupId: string | undefined): IMessageWidgetConfig[] {
        const localeConfigs = self.localeMessageWidgets.get(locale);
        if (localeConfigs) {
          return localeConfigs.getConfigsForGroup(tagGroupId);
        }

        return [];
      },

      getOnlyAssignedWidgets(tagGroupId: string): IMessageWidgetConfig[] {
        return Array.from(self.localeMessageWidgets.values()).reduce<IMessageWidgetConfig[]>(
          (acc, localeConfigs) => {
            const widgets = localeConfigs.getConfigsAssignedForGroup(tagGroupId);
            return [...acc, ...widgets];
          },
          []
        );
      },

      getLocaleMessageWidgets(locale: LanguageOption): IMessageWidgetConfig[] {
        const localeConfigs = self.localeMessageWidgets.get(locale);
        if (localeConfigs) {
          return localeConfigs.getAllConfigs();
        }

        return [];
      },

      getAllWidgets(): IMessageWidgetConfig[] {
        return Array.from(self.localeMessageWidgets.values()).reduce<IMessageWidgetConfig[]>(
          (acc, localeMessageWidgets) => {
            return [...acc, ...localeMessageWidgets.getAllConfigs()];
          },
          []
        );
      }
    };

    return views;
  })
  .actions((self) => {
    const actions = {
      setLocaleConfigs(locale: string, configs: (ApiMessageWidgetConfig | IMessageWidgetConfig)[]) {
        const localeMessageWidgets = LocaleMessageWidgetsMap.create({ locale });
        localeMessageWidgets.setConfigs(
          configs.map((c) => MessageWidgetConfig.create(parseMessageWidgetConfig(c)))
        );

        self.localeMessageWidgets.set(locale, localeMessageWidgets);
      },

      loadMessageWidgets: flow(function* () {
        try {
          const promises = Object.values(LanguageOption).map(async (locale) => {
            const configs = await self.spinnerService.getMessageWidgets(locale);

            actions.setLocaleConfigs(locale, configs);
          });

          yield Promise.all(promises);
        } catch (e) {
          logger.error({ message: "Couldn't load org message widgets", error: e });
        }
      }),

      patchExistingConfigs(configsToUpdate: IMessageWidgetConfig[]) {
        const groupedByLocale = configsToUpdate.reduce<Record<string, IMessageWidgetConfig[]>>(
          (acc, el) => {
            if (!acc[el.locale]) {
              acc[el.locale] = [];
            }

            acc[el.locale]!.push(el);

            return acc;
          },
          {}
        );

        Object.entries(groupedByLocale).forEach(([locale, updatedWidgets]) => {
          const store = self.localeMessageWidgets.get(locale);

          if (store) {
            const all = store.getAllConfigs();

            const updated = all.map((messageWidgetConfig) => {
              const maybeUpdatedWidgetConfig = updatedWidgets.find(
                (el) => el.id === messageWidgetConfig.id
              );
              return maybeUpdatedWidgetConfig || messageWidgetConfig;
            });

            actions.setLocaleConfigs(locale, updated);
          }
        });
      },

      sendMessageWidgetForm: flow(function* (widgetId: string, roomId: string) {
        try {
          yield self.spinnerService.sendMessageWidgetForm({ widgetId, roomId });
        } catch (e) {
          if ((e as Response).status === HttpStatusCode.CONFLICT) {
            const { error }: { error: WidgetSendFailedReason; message: string } = yield (
              e as Response
            ).json();
            switch (error) {
              case WidgetSendFailedReason.PreviousUnsubmitted:
                self.uiStore.setChatNotification({
                  message: intl.formatMessage("conversation.error.form.previousUnsubmitted.title")
                });
                return;
              case WidgetSendFailedReason.SendIntervalNotPassed:
                self.uiStore.setChatNotification({
                  message: intl.formatMessage("conversation.error.form.sendInterval.title")
                });
                return;
              default:
                break;
            }
          }
          self.uiStore.addGenericErrorToast();
          logger.error({ message: "Could not send message widget to room:", roomId, error: e });
        }
      })
    };

    return actions;
  });

enum WidgetSendFailedReason {
  SendIntervalNotPassed = "send_interval_not_passed",
  PreviousUnsubmitted = "previous_unsubmitted",
  WidgetNotAllowed = "widget_not_allowed",
  WidgetNotAvailable = "widget_not_available"
}

export interface IMessageWidgetsStore extends Instance<typeof MessageWidgetsStore> {}
